"use client";

import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import {
	RiEyeFill,
	RiFlagFill,
	RiHeartFill,
	RiLiveFill,
	RiMicFill
} from "@remixicon/react";
import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useMemo } from "react";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { SetSocialButtons } from "@/components/shared/set-social-buttons";
import { SocialButtonGroup } from "@/components/shared/social-button-group";
import { SpotifyPreviewPill } from "@/components/shared/spotify-preview-pill";
import { useRealtimeSupportingSetReview } from "@/hooks/features/show/use-realtime-supporting-set-review";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { renderStarRating } from "@/lib/utils/set-card-helpers";
import type { ShowArtist } from "@/lib/types/show-types";
import { useCurrentUser } from "@/stores";

interface ShowSupportingArtistCardProps {
	showArtist: ShowArtist;
	className?: string;
	showSocialLinks?: {
		youtube?: string | null;
		soundcloud?: string | null;
		appleMusic?: string | null;
		oneThousandOneTracklists?: string | null;
		setlistFm?: string | null;
	};
}

export const ShowSupportingArtistCard = memo<ShowSupportingArtistCardProps>(
	function ShowSupportingArtistCard({ showArtist, className, showSocialLinks }) {
		const preferences = useUserPreferences();
		const currentUser = useCurrentUser();
		const { artist, time_start, time_end } = showArtist;

		// Use real-time hook for instant updates
		const { review: userReview } = useRealtimeSupportingSetReview({
			showId: showArtist.show_id || "",
			showArtistId: showArtist.id,
			artistId: showArtist.artist_id || artist?.id || "",
			userId: currentUser?.id || null,
			enabled: !!currentUser?.id && !!showArtist.id,
		});

		const timeRange = useMemo(() => {
			if (!time_start) return null;

			try {
				// time_start and time_end are now full timestamps from database
				return formatTimeRangeWithPreferences(
					time_start,
					time_end || undefined,
					preferences,
				);
			} catch {
				return null;
			}
		}, [time_start, time_end, preferences]);

		if (!artist) {
			return null;
		}

		const hasGenres = Boolean(
			(Array.isArray(artist.spotify_genres) && artist.spotify_genres.length > 0) ||
			(Array.isArray(artist.manual_genres) && artist.manual_genres.length > 0)
		);
		const hasPreview = Boolean(artist.spotify_id);
		const hasReviewContent = userReview && ((userReview.rating > 0 || userReview.loved) || userReview.content);
		const hasSetLinks = showSocialLinks && (
			showSocialLinks.youtube ||
			showSocialLinks.soundcloud ||
			showSocialLinks.appleMusic ||
			showSocialLinks.oneThousandOneTracklists ||
			showSocialLinks.setlistFm
		);

		return (
			<Card className={cn("w-full border bg-card", className)}>
				<CardHeader className="p-3">
					<div className="flex items-start justify-between">
						<div className="flex min-w-0 flex-1 items-start gap-3">
							<ClickableArtistPhoto
								href={`/artists/${createArtistSlug(artist.name, artist.slug)}`}
								name={artist.name}
								imageUrl={artist.image_url}
								size="md"
								rounded="card"
								trackingData={{
									artistId: artist.id,
									artistName: artist.name,
									source: "discover",
								}}
							/>

							<div className="min-w-0 flex-1 flex flex-col gap-1">
								<div className="flex items-start justify-between">
									<div className="min-w-0 flex-1">
										<h4 className="truncate font-semibold text-sm leading-tight">
											{artist.name}
										</h4>

										{(timeRange || !time_start) && (
											<div className="text-muted-foreground text-xs">
												{timeRange || "Set Time: TBA"}
											</div>
										)}
									</div>

									{userReview && (
										<div className="ml-2 flex flex-col gap-1">
											{userReview.mustSee && (
												<div className="flex items-center justify-center gap-1 rounded-lg bg-radix-violet-9 px-2 py-1 text-radix-violet-1 text-xs">
													<RiFlagFill className="h-3.5 w-3.5" />
													<span>Must See</span>
												</div>
											)}
											{userReview.wantToSee && !userReview.mustSee && (
												<div className="flex items-center justify-center gap-1 rounded-lg bg-radix-plum-9 px-2 py-1 text-radix-plum-1 text-xs">
													<RiEyeFill className="h-3.5 w-3.5" />
													<span>Want to See</span>
												</div>
											)}
											{userReview.sawLive ? (
												<div className="flex items-center justify-center gap-1 rounded-lg bg-radix-indigo-9 px-2 py-1 text-radix-indigo-1 text-xs">
													<RiMicFill className="h-3.5 w-3.5" />
													<span>Saw Live</span>
												</div>
											) : userReview.sawStream ? (
												<div className="flex items-center justify-center gap-1 rounded-lg bg-radix-ruby-9 px-2 py-1 text-radix-ruby-1 text-xs">
													<RiLiveFill className="h-3.5 w-3.5" />
													<span>Saw Stream</span>
												</div>
											) : null}
										</div>
									)}
								</div>

								<SocialButtonGroup
									name={artist.name}
									links={{
										spotify: artist.spotify_url || null,
										youtube: artist.youtube_url || null,
										instagram: artist.instagram_url || null,
										twitter: artist.twitter_url || null,
										website: artist.website_url || null,
									}}
									size="xs"
									maxVisible={3}
								/>

								{/* Set Social Links (setlist.fm, etc.) */}
								{hasSetLinks && showSocialLinks && (
									<SetSocialButtons
										setName={`${artist.name} set`}
										links={showSocialLinks}
										size="xs"
									/>
								)}
							</div>
						</div>
					</div>
				</CardHeader>

				{(hasGenres || hasPreview || hasReviewContent) && (
					<CardContent className="px-3 pt-0 pb-3">
						<div className="space-y-3">
							{/* Genres and Preview - aligned with artist info (after photo) */}
							{(hasGenres || hasPreview) && (
								<div className="flex gap-3">
									{/* Spacer to align with photo width (48px = w-12) */}
									<div className="w-12 flex-shrink-0" />
									<div className={cn("flex-1", hasGenres && hasPreview ? "grid grid-cols-2 gap-4" : "")}>
										{hasGenres && (
											<div className="space-y-1">
												<Label>Genres</Label>
												<GenreBadgeListFromData
													spotifyGenres={artist.spotify_genres || null}
													manualGenres={artist.manual_genres || null}
													maxDisplay={3}
													badgeClassName="h-5 px-2 text-xs"
												/>
											</div>
										)}

										{hasPreview && (
											<div className="space-y-1">
												<Label>Preview</Label>
												<SpotifyPreviewPill
													spotifyId={artist.spotify_id!}
													artistName={artist.name}
												/>
											</div>
										)}
									</div>
								</div>
							)}

							{/* User review content */}
							{hasReviewContent && (
								<div className="flex gap-3">
									<div className="w-12 flex-shrink-0" />

									<div className="flex-1 space-y-1">
										{(userReview.rating > 0 || userReview.loved) && (
											<div className="-ml-0.5 flex items-center gap-1">
												{userReview.rating > 0 && (
													<div className="flex items-center">
														{renderStarRating({
															rating: userReview.rating,
															size: "lg",
														})}
													</div>
												)}
												{userReview.loved && (
													<RiHeartFill className="h-5 w-5 text-red-500" />
												)}
											</div>
										)}

										{userReview.content && (
											<p className="text-left text-sm">
												{userReview.content}
											</p>
										)}
									</div>
								</div>
							)}
						</div>
					</CardContent>
				)}
			</Card>
		);
	},
);
